import { useEffect, useState } from 'react';
import {
  subscribeAuditEvents,
  type AuditEventListResult,
  type AuditFilters,
} from '@/lib/firestoreAudit';

export type UseAuditEventsState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; result: AuditEventListResult }
  | { kind: 'ERROR'; error: unknown };

/**
 * Assina o log de auditoria `/audit` com os filtros dados e expõe o estado da
 * leitura (LOADING / READY / ERROR).
 *
 * Origem: sem issue — commit efa5a8ca2, 2026-04-26 17:22 +0200 (Sprint 2).
 *
 * Contrato: reassina a cada novo objeto `filters` (o chamador precisa
 * memoizar, senão é um listener novo por render). READY traz
 * `{ events, truncated }` (até `AUDIT_LIST_LIMIT`, `at desc`); ERROR traz o
 * erro do snapshot (ex.: `failed-precondition` por índice faltando).
 *
 * Usado por (admin): `AuditList` (`routes/AuditPage.tsx`, `TenantAuditTab`),
 * `features/superAdmins/useSuperAdminAuditTrail.ts`,
 * `features/notifications/BroadcastHistory.tsx`.
 *
 * Arquitetura: só leitura; regra de quem filtra e índices em
 * `subscribeAuditEvents` (`lib/firestoreAudit.ts`). Escrita do `/audit` é só
 * das Functions (`logAuditEvent`).
 *
 * Dados: `AuditEventRecord` = `AuditEventSchema` + `id` + `atMillis` (ver
 * `lib/firestoreAudit.ts`).
 *
 * Dicas: o ERROR não gera report de telemetria aqui nem no `AuditList` (só
 * toast) — se o listener quebrar em produção, o PostHog não fica sabendo
 * (achado da #936).
 *
 * Histórico:
 *  - 2026-10-10 15:12 +0200 — (#936) cabeçalho padrão (#796).
 */
export function useAuditEvents(filters: AuditFilters): UseAuditEventsState {
  const [state, setState] = useState<UseAuditEventsState>({ kind: 'LOADING' });

  useEffect(() => {
    setState({ kind: 'LOADING' });
    const unsubscribe = subscribeAuditEvents(
      filters,
      (result) => {
        setState({ kind: 'READY', result });
      },
      (error) => {
        setState({ kind: 'ERROR', error });
      },
    );
    return unsubscribe;
  }, [filters]);

  return state;
}
